/**
 * TG Theme Bridge
 * Binds all theme_params as --tg-theme-* CSS vars on :root.
 * Syncs native chrome (header, bg, bottom bar) with app theme.
 */

const PARAMS = [
  "bg_color", "text_color", "hint_color", "link_color",
  "button_color", "button_text_color", "secondary_bg_color",
  "header_bg_color", "bottom_bar_bg_color", "accent_text_color",
  "section_bg_color", "section_header_text_color",
  "section_separator_color", "subtitle_text_color",
  "destructive_text_color",
] as const;

const toKebab = (s: string) => s.replace(/_/g, "-");

/** Inject all TG theme params as --tg-theme-* CSS vars */
export function bindTgVars(): void {
  const tp = (window as /* eslint-disable */ any).Telegram?.WebApp?.themeParams;
  if (!tp) return;
  const root = document.documentElement;
  for (const key of PARAMS) {
    const val = tp[key as string];
    if (val) root.style.setProperty(`--tg-theme-${toKebab(key)}`, val);
  }
}

/** Sync TG native chrome with resolved --cg-bg-app */
export function syncChrome(): void {
  const tg = (window as /* eslint-disable */ any).Telegram?.WebApp;
  if (!tg) return;
  const bg = getComputedStyle(document.documentElement)
    .getPropertyValue("--cg-bg-app").trim();
  if (!bg) return;
  tg.setBackgroundColor?.(bg);
  tg.setHeaderColor?.(bg);
  tg.setBottomBarColor?.(bg);
}

/** Light values for --tg-theme-* (iOS system colors) */
const LIGHT: Record<string, string> = {
  "bg-color": "#ffffff", "secondary-bg-color": "#f2f2f7",
  "text-color": "#1c1c1e", "hint-color": "#8e8e93",
  "link-color": "#007aff", "button-color": "#007aff",
  "button-text-color": "#ffffff", "section-bg-color": "#ffffff",
  "section-header-text-color": "#8e8e93", "subtitle-text-color": "#8e8e93",
  "destructive-text-color": "#ff3b30", "header-bg-color": "#f2f2f7",
  "accent-text-color": "#007aff",
  "section-separator-color": "rgba(0,0,0,0.06)",
  "bottom-bar-bg-color": "#f2f2f7",
};

let _savedDark: Record<string, string> = {};

/** Override --tg-theme-* inline styles for light mode */
export function forceLightVars(): void {
  const root = document.documentElement;
  // Save current (dark) values before overriding
  _savedDark = {};
  for (const key of Object.keys(LIGHT)) {
    _savedDark[key] = root.style.getPropertyValue(`--tg-theme-${key}`);
    root.style.setProperty(`--tg-theme-${key}`, LIGHT[key]);
  }
}

/** Restore original --tg-theme-* from TG SDK */
export function restoreDarkVars(): void {
  const root = document.documentElement;
  for (const [key, val] of Object.entries(_savedDark)) {
    if (val) root.style.setProperty(`--tg-theme-${key}`, val);
    else root.style.removeProperty(`--tg-theme-${key}`);
  }
  // Re-bind from SDK to be safe
  bindTgVars();
}
